/**
 * SalonHub 顧客管理APIサービス
 *
 * @description 顧客CRUD・検索・フィルタリング機能を提供
 * @backend NestJS + PostgreSQL統合
 */

import logger from '../utils/logger';
import env from '../config/env';
import { loadEncryptedToken } from '../utils/crypto';

// API設定
const API_CONFIG = {
  BASE_URL: env.getApiConfig().baseUrl,
  ENDPOINTS: {
    CUSTOMERS: '/api/v1/customers',
    CUSTOMER_BY_ID: (id: string) => `/api/v1/customers/${id}`,
    CUSTOMER_BY_CODE: (code: string) => `/api/v1/customers/code/${code}`,
  },
};

// 顧客データ型定義
export interface Customer {
  id: string;
  customerCode: string;
  name: string;
  nameKana?: string;
  email: string;
  phone: string;
  phoneSecondary?: string;
  birthDate?: string;
  gender?: 'male' | 'female' | 'other' | 'unspecified';
  address?: string;
  memo?: string;
  rank?: 'bronze' | 'silver' | 'gold' | 'platinum';
  status?: 'active' | 'inactive';
  tags?: string[];
  bookingSource?:
    | 'hotpepper'
    | 'rakuten'
    | 'google'
    | 'instagram'
    | 'referral'
    | 'walk-in'
    | 'other';
  firstVisitDate?: string;
  lastVisit?: string;
  totalVisits: number;
  totalSpent: number;
  preferredStaff?: string;
  medicalInfo?: {
    allergies?: string[];
    contraindications?: string[];
    skinType?: string;
    preferences?: string;
    healthConditions?: string[];
  };
  isVip: boolean;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCustomerDto {
  name: string;
  nameKana?: string;
  email: string;
  phone: string;
  phoneSecondary?: string;
  birthDate?: string;
  gender?: 'male' | 'female' | 'other' | 'unspecified';
  address?: string;
  memo?: string;
  rank?: 'bronze' | 'silver' | 'gold' | 'platinum';
  tags?: string[];
  bookingSource?:
    | 'hotpepper'
    | 'rakuten'
    | 'google'
    | 'instagram'
    | 'referral'
    | 'walk-in'
    | 'other';
  preferredStaff?: string;
  medicalInfo?: {
    allergies?: string[];
    contraindications?: string[];
    skinType?: string;
    preferences?: string;
    healthConditions?: string[];
  };
  isVip?: boolean;
}

export interface UpdateCustomerDto extends Partial<CreateCustomerDto> {
  status?: 'active' | 'inactive';
}

export interface QueryCustomerDto {
  page?: number;
  limit?: number;
  rank?: 'bronze' | 'silver' | 'gold' | 'platinum';
  status?: 'active' | 'inactive';
  search?: string;
  staffName?: string;
  isVip?: boolean;
}

export interface CustomerListResponse {
  data: Customer[];
  total: number;
  page: number;
  limit: number;
}

class CustomersService {
  /**
   * 認証ヘッダーを取得
   */
  private async getAuthHeaders(): Promise<HeadersInit> {
    const token = loadEncryptedToken();
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * 顧客一覧を取得
   */
  async getCustomers(query?: QueryCustomerDto): Promise<CustomerListResponse> {
    try {
      const params = new URLSearchParams();
      if (query) {
        Object.entries(query).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, String(value));
          }
        });
      }

      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMERS}?${params}`;
      const headers = await this.getAuthHeaders();

      logger.debug('Fetching customers', { url, query });

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch customers: ${response.statusText}`);
      }

      const data = await response.json();
      logger.info('Customers fetched successfully', { total: data.total });
      return data;
    } catch (error) {
      logger.error('Error fetching customers', { error });
      throw error;
    }
  }

  /**
   * 顧客詳細を取得（ID指定）
   */
  async getCustomerById(id: string): Promise<Customer> {
    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMER_BY_ID(id)}`;
      const headers = await this.getAuthHeaders();

      logger.debug('Fetching customer by ID', { id, url });

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch customer: ${response.statusText}`);
      }

      const data = await response.json();
      logger.info('Customer fetched successfully', { id });
      return data;
    } catch (error) {
      logger.error('Error fetching customer', { error, id });
      throw error;
    }
  }

  /**
   * 顧客詳細を取得（顧客コード指定）
   */
  async getCustomerByCode(code: string): Promise<Customer> {
    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMER_BY_CODE(code)}`;
      const headers = await this.getAuthHeaders();

      logger.debug('Fetching customer by code', { code, url });

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch customer: ${response.statusText}`);
      }

      const data = await response.json();
      logger.info('Customer fetched successfully', { code });
      return data;
    } catch (error) {
      logger.error('Error fetching customer by code', { error, code });
      throw error;
    }
  }

  /**
   * 新規顧客を登録
   */
  async createCustomer(dto: CreateCustomerDto): Promise<Customer> {
    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMERS}`;
      const headers = await this.getAuthHeaders();

      logger.debug('Creating customer', { url, dto });

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(dto),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to create customer: ${response.statusText}`
        );
      }

      const data = await response.json();
      logger.info('Customer created successfully', { id: data.id });
      return data;
    } catch (error) {
      logger.error('Error creating customer', { error, dto });
      throw error;
    }
  }

  /**
   * 顧客情報を更新
   */
  async updateCustomer(id: string, dto: UpdateCustomerDto): Promise<Customer> {
    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMER_BY_ID(id)}`;
      const headers = await this.getAuthHeaders();

      logger.debug('Updating customer', { url, id, dto });

      const response = await fetch(url, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(dto),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to update customer: ${response.statusText}`
        );
      }

      const data = await response.json();
      logger.info('Customer updated successfully', { id });
      return data;
    } catch (error) {
      logger.error('Error updating customer', { error, id, dto });
      throw error;
    }
  }

  /**
   * 顧客を削除
   */
  async deleteCustomer(id: string): Promise<void> {
    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMER_BY_ID(id)}`;
      const headers = await this.getAuthHeaders();

      logger.debug('Deleting customer', { url, id });

      const response = await fetch(url, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to delete customer: ${response.statusText}`);
      }

      logger.info('Customer deleted successfully', { id });
    } catch (error) {
      logger.error('Error deleting customer', { error, id });
      throw error;
    }
  }
}

// シングルトンインスタンスをエクスポート
export default new CustomersService();
